import { useRef } from 'react'

import { IconInbox, IconUpload } from '@tabler/icons-react'
import { Upload, UploadProps } from 'antd'
import { RcFile, UploadListType } from 'antd/lib/upload/interface'

import { Button } from '../shared-components/BUI/Button'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'

import { ACCEPT, MAX_SIZE_MB, formatAccept } from '@/config/constants/fileTypes'
import { usePreventSaving } from '@/context/preventSavingContext'
import {
  AttachmentCompressionOptions,
  FileUploadResult,
  useAttachmentUpload,
} from '@/hooks/useAttachmentUpload'

import { buildPassTheme } from 'src/styles/theme'

import {
  AttachmentType,
  CreateAttachmentUiMutationVariables,
  Resource,
} from 'types/api.graphql'

type Props = Omit<UploadProps, 'onChange' | 'children'> & {
  projectId: string | null // used to link the attachment to the project, 99% of cases you want to supply this - but not for e.g. profile pics
  attachmentType: AttachmentType
  attachmentResource: Resource
  attachmentSortDate?: string
  listType?: UploadListType
  createAttachment?: boolean
  fileList?: FileUploadResult[]
  onChange?: (value: FileUploadResult[]) => void
  accept?: string
  compact?: boolean
  renderFileList?: (
    fileList: FileUploadResult[],
    onRemove: (file: RcFile) => void,
    insert: (newFile: RcFile, oldFile?: FileUploadResult) => Promise<void>
  ) => React.ReactNode
  fileListPosition?: 'top' | 'bottom'
  isPublic?: boolean
  mutationVariables?: Partial<CreateAttachmentUiMutationVariables['input']>
  compressionPreset?: AttachmentCompressionOptions['preset']
  onUploadStatusChange?: (status: 'uploading' | 'done' | 'error') => void
  children?:
    | React.ReactNode
    | ((
        fileList: FileUploadResult[],
        onRemove: (file: RcFile) => void
      ) => React.ReactNode)
}

export const AttachmentUploader: React.FC<Props> = ({
  projectId,
  attachmentType,
  attachmentResource,
  attachmentSortDate,
  listType = 'picture',
  createAttachment = true,
  fileList = [],
  onChange,
  accept = ACCEPT,
  compact,
  renderFileList,
  fileListPosition = 'bottom',
  isPublic,
  mutationVariables,
  children,
  compressionPreset,
  onUploadStatusChange,
  ...rest
}) => {
  const memoFileList = useRef(fileList) // required for multi-upload
  const { uploadAttachment, validateFileSize, validateFileType } =
    useAttachmentUpload()
  const { setIsSavingBlocked } = usePreventSaving()

  const single = !rest.multiple || rest.maxCount === 1

  const addFile = (file: RcFile) => {
    const result: FileUploadResult = {
      uid: file.uid,
      name: file.name,
      status: 'uploading',
      key: null,
    }

    const newFiles = single ? [result] : [...memoFileList.current, result]
    memoFileList.current = newFiles
    onChange(newFiles)
  }

  const onUpload = (result: FileUploadResult) => {
    const newFiles = single
      ? [result]
      : [...memoFileList.current.filter((f) => f.uid !== result.uid), result]

    memoFileList.current = newFiles
    onChange(newFiles)
  }

  const onRemove = (file: RcFile) => {
    const fileIndex = fileList.findIndex((f) => f.uid === file.uid)
    const newFileList = fileList.reduce((agg, curr, i) => {
      if (fileIndex === i) return agg
      return [...agg, { ...curr }]
    }, [])
    memoFileList.current = newFileList
    onChange(newFileList)
  }

  const onInsertFileUpload = (
    newFile: FileUploadResult,
    oldFile?: FileUploadResult
  ) => {
    if (!oldFile) {
      const newFileList = [...memoFileList.current, newFile]
      memoFileList.current = newFileList
      onChange(newFileList)
      return
    }

    const fileIndex = fileList.findIndex((f) => f.uid === oldFile.uid)
    const newFileList = fileList.reduce((agg, curr, i) => {
      if (fileIndex === i) return [...agg, { ...curr }, { ...newFile }]
      return [...agg, { ...curr }]
    }, [])

    memoFileList.current = newFileList
    onChange(newFileList)
  }

  const insert = async (newFile: RcFile, oldFile?: FileUploadResult) => {
    return uploadAttachment(newFile, {
      attachmentType,
      attachmentResource,
      attachmentSortDate,
      createAttachment,
      onUpload: (file) => onInsertFileUpload(file, oldFile),
      onProgress: progressChangeHandler,
      isPublic,
      mutationVariables: { ...mutationVariables, projectId },
      compressionOptions: compressionPreset
        ? {
            preset: compressionPreset,
          }
        : undefined,
    })
  }

  const progressChangeHandler = (event, file) => {
    const newFiles = memoFileList.current.map((f) =>
      f.uid === file.uid ? { ...f, percent: event.percent } : { ...f }
    )

    memoFileList.current = newFiles
    onChange(newFiles)
  }

  const uploadProps = {
    listType,
    fileList,
    progress: {
      strokeWidth: 3,
      strokeColor: buildPassTheme.colour.primary.base,
      format: (percent) => `${parseFloat(percent.toFixed(2))}%`,
    },
    beforeUpload: (file) => {
      if (!validateFileSize()(file)) {
        return Upload.LIST_IGNORE
      }

      if (!validateFileType(accept)(file)) {
        return Upload.LIST_IGNORE
      }

      if (single) {
        memoFileList.current = []
      }

      if (memoFileList.current.length >= rest.maxCount) {
        return Upload.LIST_IGNORE
      }

      addFile(file)
      return true
    },
    onRemove,
    customRequest: async ({ file, onSuccess }) => {
      onUploadStatusChange?.('uploading')
      setIsSavingBlocked(true)
      await uploadAttachment(file as RcFile, {
        attachmentType,
        attachmentResource,
        attachmentSortDate,
        createAttachment,
        onUpload,
        onProgress: progressChangeHandler,
        isPublic,
        mutationVariables: { ...mutationVariables, projectId },
        compressionOptions: compressionPreset
          ? {
              preset: compressionPreset,
            }
          : undefined,
      })
        .then(() => {
          onUploadStatusChange?.('done')
        })
        .catch(() => {
          onUploadStatusChange?.('error')
        })
        .finally(() => {
          setIsSavingBlocked(false)
        })
      onSuccess(null, null)
    },
  } as UploadProps

  const uploaderContent = () => {
    if (typeof children === 'function') {
      return children(memoFileList.current, onRemove)
    }

    if (compact) {
      if (fileList?.length < rest.maxCount) {
        return (
          <Button
            size="small"
            variant="secondary"
            icon={<IconUpload size={14} />}
          >
            Upload
          </Button>
        )
      }
      return null
    }

    return (
      <div style={{ padding: '0.5rem' }}>
        <Flexbox justifyContent="center">
          <IconInbox
            size={42}
            stroke={1.5}
            color={buildPassTheme.colour.primary.base}
          />
        </Flexbox>
        <Text variant="label02">Click or drag file to this area to upload</Text>
        <Text variant="caption01" secondary="_500" mt="0.5rem">
          Supported formats: {formatAccept(accept)}, with a maximum size of{' '}
          {MAX_SIZE_MB}MB
        </Text>
        {rest.maxCount > 1 && (
          <Text variant="caption01" secondary="_500" mt="0.5rem">
            Maximum of {rest.maxCount} files per batch
          </Text>
        )}
      </div>
    )
  }

  const UploaderComponent = compact ? Upload : Upload.Dragger

  return (
    <>
      {renderFileList &&
        fileListPosition === 'top' &&
        renderFileList(memoFileList.current, onRemove, insert)}

      <UploaderComponent
        {...rest}
        {...uploadProps}
        showUploadList={!renderFileList}
      >
        {uploaderContent()}
      </UploaderComponent>

      {renderFileList &&
        fileListPosition === 'bottom' &&
        renderFileList(memoFileList.current, onRemove, insert)}
    </>
  )
}
